import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Search, GripVertical, DollarSign, Calendar, User } from 'lucide-react';
import { toast } from 'sonner';

const STAGES = ['lead', 'qualified', 'proposal', 'won', 'lost'];
const STAGE_LABELS = { lead: 'Lead', qualified: 'Qualified', proposal: 'Proposal', won: 'Won', lost: 'Lost' };
const STAGE_COLORS = { lead: 'bg-blue-500/10 text-blue-700 dark:text-blue-400', qualified: 'bg-purple-500/10 text-purple-700 dark:text-purple-400', proposal: 'bg-amber-500/10 text-amber-700 dark:text-amber-400', won: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400', lost: 'bg-red-500/10 text-red-700 dark:text-red-400' };

export default function OpportunitiesPage() {
  const [opps, setOpps] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [dlgOpen, setDlgOpen] = useState(false);
  const [form, setForm] = useState({ title: '', customer_id: '', value: '', expected_close_date: '', assigned_to: '', description: '', stage: 'lead' });
  const [dragId, setDragId] = useState(null);

  const load = useCallback(() => {
    api.get('/opportunities', { params: { search } }).then(r => setOpps(r.data.opportunities || []));
  }, [search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/users').then(r => setUsers(r.data.users || []));
    api.get('/customers').then(r => setCustomers(r.data.customers || []));
  }, []);

  const create = async () => {
    try {
      await api.post('/opportunities', { ...form, value: parseFloat(form.value) || 0 });
      toast.success('Opportunity created');
      setDlgOpen(false);
      setForm({ title: '', customer_id: '', value: '', expected_close_date: '', assigned_to: '', description: '', stage: 'lead' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const moveStage = async (oppId, newStage) => {
    try {
      await api.put(`/opportunities/${oppId}/stage`, { stage: newStage });
      load();
    } catch (e) { toast.error('Move failed'); }
  };

  const handleDrop = (stage) => (e) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain');
    if (id) moveStage(id, stage);
    setDragId(null);
  };

  const grouped = STAGES.reduce((acc, s) => { acc[s] = opps.filter(o => o.stage === s); return acc; }, {});

  const getUserName = (id) => users.find(u => u.user_id === id)?.name || '';
  const getCustName = (id) => customers.find(c => c.customer_id === id)?.name || '';

  return (
    <div data-testid="opportunities-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Opportunities</h1>
          <p className="text-sm text-muted-foreground mt-1">{opps.length} total opportunities</p>
        </div>
        <Button onClick={() => setDlgOpen(true)} data-testid="create-opportunity-btn"><Plus size={16} className="mr-1" /> New Opportunity</Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
        <Input placeholder="Search opportunities..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="opp-search" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-testid="kanban-board">
        {STAGES.map(stage => (
          <div key={stage}
            className={`rounded-md border border-border bg-muted/30 p-3 min-h-[400px] transition-colors ${dragId ? 'border-dashed' : ''}`}
            onDragOver={e => e.preventDefault()} onDrop={handleDrop(stage)} data-testid={`kanban-col-${stage}`}>
            <div className="flex items-center justify-between mb-3">
              <Badge variant="secondary" className={`${STAGE_COLORS[stage]} font-medium text-xs`}>{STAGE_LABELS[stage]}</Badge>
              <span className="text-xs font-medium text-muted-foreground">{grouped[stage]?.length || 0}</span>
            </div>
            <div className="space-y-2">
              {(grouped[stage] || []).map(opp => (
                <Card key={opp.opportunity_id} draggable onDragStart={e => { e.dataTransfer.setData('text/plain', opp.opportunity_id); setDragId(opp.opportunity_id); }} onDragEnd={() => setDragId(null)}
                  className={`cursor-grab active:cursor-grabbing border border-border hover:border-primary/30 transition-all ${dragId === opp.opportunity_id ? 'opacity-50 rotate-1' : ''}`}
                  data-testid={`opp-card-${opp.opportunity_id}`}>
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-start gap-1">
                      <GripVertical size={14} className="text-muted-foreground mt-0.5 shrink-0" />
                      <span className="text-sm font-medium leading-tight">{opp.title}</span>
                    </div>
                    {opp.value > 0 && <div className="flex items-center gap-1 text-xs text-muted-foreground"><DollarSign size={12} />${opp.value.toLocaleString()}</div>}
                    {opp.expected_close_date && <div className="flex items-center gap-1 text-xs text-muted-foreground"><Calendar size={12} />{opp.expected_close_date}</div>}
                    {opp.assigned_to && <div className="flex items-center gap-1 text-xs text-muted-foreground"><User size={12} />{getUserName(opp.assigned_to)}</div>}
                    {opp.customer_id && <div className="text-xs text-muted-foreground">{getCustName(opp.customer_id)}</div>}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="create-opp-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Opportunity</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="space-y-1.5"><Label>Title *</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="opp-title-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Value ($)</Label><Input type="number" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} data-testid="opp-value-input" /></div>
              <div className="space-y-1.5"><Label>Close Date</Label><Input type="date" value={form.expected_close_date} onChange={e => setForm(f => ({ ...f, expected_close_date: e.target.value }))} data-testid="opp-date-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Customer</Label>
                <Select value={form.customer_id} onValueChange={v => setForm(f => ({ ...f, customer_id: v }))}>
                  <SelectTrigger data-testid="opp-customer-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{customers.map(c => <SelectItem key={c.customer_id} value={c.customer_id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Assign To</Label>
                <Select value={form.assigned_to} onValueChange={v => setForm(f => ({ ...f, assigned_to: v }))}>
                  <SelectTrigger data-testid="opp-assign-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} data-testid="opp-desc-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={!form.title} data-testid="opp-create-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
